import { html, css } from 'lit';
import { customElement, query, state, property } from 'lit/decorators.js';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { TRANSITION_DURATION_FAST } from '../common/transition';
import { TeSegmentOption } from './te-segment-option';
import { styleMap } from 'lit/directives/style-map';

export type ToggleVariant = 'filled' | 'underlined';

/**
 * Segment Component
 *
 * @slot - Default slot for rendering segment options
 */
@customElement('te-segment')
export class TeSegment extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-segment {
        display: flex;
        grid-gap: 0px;
        position: relative;
        width: fit-content;
        background: ${theme.colors.shade0};
        box-shadow: ${theme.shadows.secondary};
        border-radius: 4px;
      }

      .te-segment.rounded {
        border-radius: 24px;
      }

      .te-segment.vertical {
        flex-direction: column;
      }

      .te-segment-highlight-wrapper {
        position: absolute;
        transition-duration: ${TRANSITION_DURATION_FAST}ms;
        left: 0;
        top: 0;
        height: 40px;
        margin: 2px;
      }

      .te-segment-highlight {
        border-radius: 4px;
      }

      .te-segment-highlight.rounded {
        border-radius: 24px;
      }

      .te-segment-highlight-wrapper .te-segment-highlight {
        display: flex;
        height: 36px;
        width: calc(100% - 4px);
        background: ${theme.colors.primary.v0};
        box-shadow: ${theme.shadows.secondary};
      }

      ::slotted(*) {
        max-width: 160px;
        min-width: 128px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: inherit;
      }

      ::slotted(te-segment-option[icon]) {
        min-width: 64px;
      }
    `,
  ];

  @query('#te-segment-slot') _slotContent?: HTMLSlotElement;

  /** Pre-styled segment theme */
  @property({ type: String }) variant: 'default' | 'rounded' | 'vertical' = 'default';

  @state() private _items = 1;

  getChildNodes(): TeSegmentOption[] {
    if (!this._slotContent) return [];
    const nodes = this._slotContent.assignedNodes() as TeSegmentOption[];
    return nodes.filter((node) => node.nodeType === 1);
  }

  firstUpdated() {
    this._items = this.getChildNodes().length;
  }

  isVertical() {
    return this.variant === 'vertical';
  }

  isRounded() {
    return this.variant === 'rounded';
  }

  renderHighlighter() {
    const options = this.getChildNodes();
    const index = options.findIndex((node) => node.active);
    if (index === -1) return;
    const left = (index * 100) / this._items;
    const top = (index * 100) / this._items;
    const highlightClasses = [
      'te-segment-highlight',
      this.isVertical() && 'vertical',
      this.isRounded() && 'rounded',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div
        class="te-segment-highlight-wrapper"
        style=${styleMap(this.getHighlightWrapperStyle(left, top))}
      >
        <div class=${highlightClasses}></div>
      </div>
    `;
  }

  getHighlightWrapperStyle(left: number, top: number) {
    const vertical = this.isVertical();
    return {
      left: vertical ? '0' : `${left}%`,
      top: vertical ? `${top}%` : '0',
      width: vertical ? '100%' : `${100 / this._items}%`,
      transitionProperty: vertical ? 'top' : 'left',
    };
  }

  render() {
    const segmentClasses = [
      'te-segment',
      this.isRounded() ? 'rounded' : '',
      this.isVertical() ? 'vertical' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return html`
      <div class="te-segment-wrapper">
        <div class=${segmentClasses}>
          <slot id="te-segment-slot"></slot>
          ${this.renderHighlighter()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-segment': TeSegment;
  }
}
